import type { components } from '@/shared/api/schema'
import { invoiceReadPermissions } from './invoiceForm'
import { paymentReadPermissions } from './paymentForm'

export const supplierActivityPermissions = [...new Set([...invoiceReadPermissions, ...paymentReadPermissions])]
export type SupplierActivity = components['schemas']['SupplierActivityOutput']
export type ActivityRow = components['schemas']['SupplierActivityRow']
export const activityLabels = {
  opening_payable: ['期初未结应付', 'Opening unpaid payables'],
  period_payable: ['本期应付净增加', 'Net payables added in period'],
  period_allocated: ['本期核销货款', 'Allocated in period'],
  closing_payable: ['期末未结应付', 'Closing unpaid payables'],
  opening_advance: ['期初未核销预付', 'Opening unapplied prepayments'],
  period_cash: ['本期实际付款', 'Actual cash paid in period'],
  closing_advance: ['期末未核销预付', 'Closing unapplied prepayments'],
  confirmed_at_cutoff: ['截至期末可见发票累计确认', 'Visible invoice amounts confirmed at cutoff'],
  disputed_source_at_cutoff: ['截至期末可见争议来源金额', 'Visible disputed source amounts at cutoff'],
  period_advance: ['本期预付净变动', 'Net prepayment change'],
  opening_credit: ['期初未用贷项', 'Opening unused credit'],
  period_credit: ['本期贷项净变动', 'Net credit change'],
  closing_credit: ['期末未用贷项', 'Closing unused credit'],
  opening_credit_hold: ['期初贷项退款占用', 'Opening credit refund hold'],
  closing_credit_hold: ['期末贷项退款占用', 'Closing credit refund hold'],
  closing_credit_available: ['期末可用贷项', 'Closing available credit'],
  opening_advance_hold: ['期初预付退款占用', 'Opening prepayment refund hold'],
  closing_advance_hold: ['期末预付退款占用', 'Closing prepayment refund hold'],
  closing_advance_available: ['期末可用预付', 'Closing available prepayment'],
  period_credit_issued: ['本期签发贷项', 'Credit issued in period'],
  period_credit_applied: ['本期应用贷项', 'Credit applied in period'],
  period_credit_refunded: ['本期贷项退款结算', 'Credit refund settled in period'],
  period_credit_restored: ['本期反向恢复贷项', 'Credit restored in period'],
  period_advance_refunded: ['本期预付退款结算', 'Prepayment refund settled in period'],
  period_advance_restored: ['本期反向恢复预付', 'Prepayment restored in period'],
  period_refund_cash_received: ['本期退款到账记录', 'Refund cash receipt records'],
  period_refund_cash_returned: ['本期真实资金退回', 'Actual refund cash returned'],
  period_refund_recording_correction: ['本期到账登记更正（非新付款）', 'Receipt corrections (not new payments)'],
  opening_net_position: ['期初净头寸', 'Opening net position'],
  period_net_position: ['本期净头寸变动', 'Net position change'],
  closing_net_position: ['期末净头寸', 'Closing net position'],
} as const
export const activityBalances = [
  { label: ['未结应付', 'Unpaid payables'], opening: 'opening_payable', change: 'period_payable', closing: 'closing_payable' },
  { label: ['未核销预付', 'Unapplied prepayments'], opening: 'opening_advance', change: 'period_advance', closing: 'closing_advance', openingHold: 'opening_advance_hold', hold: 'closing_advance_hold', available: 'closing_advance_available' },
  { label: ['未用贷项', 'Unused credit'], opening: 'opening_credit', change: 'period_credit', closing: 'closing_credit', openingHold: 'opening_credit_hold', hold: 'closing_credit_hold', available: 'closing_credit_available' },
  { label: ['净头寸', 'Net position'], opening: 'opening_net_position', change: 'period_net_position', closing: 'closing_net_position' },
] as const
export const activityCashKeys = ['period_cash', 'period_refund_cash_received', 'period_refund_cash_returned', 'period_refund_recording_correction'] as const
export const activityDetailKeys = ['period_credit_issued', 'period_credit_applied', 'period_credit_refunded', 'period_credit_restored', 'period_advance_refunded', 'period_advance_restored', 'confirmed_at_cutoff', 'disputed_source_at_cutoff'] as const
export const activityKinds: Record<string, [string, string]> = {
  payable: ['原应付', 'Original payable'], receipt_reversal: ['入库冲销', 'Receipt reversal'],
  fee_reversal: ['服务费用冲销', 'Service fee reversal'],
  invoice_adjustment: ['发票结算调整', 'Invoice settlement adjustment'],
  payment: ['实际付款', 'Actual cash payment'], legacy_payment: ['历史付款（实际日期待核实）', 'Legacy payment (actual date unverified)'],
  allocation: ['货款核销（不是再次付款）', 'Allocation (not another payment)'],
  credit_application: ['供应商贷项抵扣（非现金付款）', 'Supplier credit applied (not cash)'],
  credit_issued: ['签发贷项（资产增加）', 'Credit issued (asset increase)'],
  credit_refund_settlement: ['贷项退款结算（不是再次到账）', 'Credit refund settlement (not another receipt)'],
  credit_refund_restored: ['反向恢复贷项（原记录保留）', 'Credit restored (original facts preserved)'],
  prepayment_refund_settlement: ['预付退款结算（不是再次到账）', 'Prepayment refund settlement (not another receipt)'],
  prepayment_refund_restored: ['反向恢复预付（原记录保留）', 'Prepayment restored (original facts preserved)'],
  refund_cash_received: ['实际退款到账记录', 'Refund cash receipt record'],
  refund_cash_returned: ['真实资金退回', 'Actual cash returned'],
  refund_recording_correction: ['原到账非真实的登记更正（无新付款）', 'Original receipt correction (no new payment)'],
  refund_hold_placed: ['退款额度占用（不改账面资产）', 'Refund hold (book assets unchanged)'],
  refund_hold_released: ['退款额度释放／转结算', 'Refund hold released / settled'],
  credit_match_attribution: ['原抵扣的后补票归属（不再次抵扣）', 'Prior credit matched to invoice (not applied again)'],
  invoice_recorded: ['发票来源登记', 'Invoice source recorded'], invoice_confirmed: ['发票来源确认', 'Invoice source confirmed'],
  invoice_released: ['旧发票来源释放', 'Previous invoice source released'],
}
export function activitySource(row: ActivityRow, supplierId?: number) {
  if (row.refund_id && supplierId) return { path: '/finance/supplier-refunds', query: { supplier_id: String(supplierId), focus: String(row.refund_id),
    ...(row.refund_reversal_id ? { reversal_focus: String(row.refund_reversal_id) } : {}) } }
  if (row.invoice_id) return { path: '/finance/supplier-invoices', query: { focus: String(row.invoice_id) } }
  if (row.credit_id && supplierId) return { path: '/finance/supplier-credits', query: { supplier_id: String(supplierId),
    focus: String(row.credit_id), keyword: row.kind === 'credit_issued' || !row.source_no ? row.document_no : row.source_no } }
  if (row.instruction_id) return { path: '/finance/payment-instructions', query: { focus: String(row.instruction_id) } }
  if (row.payable_id) return { path: '/records/payables', query: { keyword: row.source_no || row.document_no } }
  if (row.payment_id) return { path: '/records/payments', query: { keyword: row.document_no } }
  return null
}
